import { useEffect, useState } from 'react';
import { AlertTriangle, Info, Loader2, Presentation } from 'lucide-react';
import { apiClient } from '../../../api';

const PROVENANCE_COLOR: Record<string, string> = { rule: '#D3D1C7', model: '#AFA9EC', human: '#5DCAA5' };

interface ProvenanceSegment {
  key: string;
  label: string;
  count: number;
  quality: { rate_text: string; note: string | null; provisional?: boolean; insufficient?: boolean };
}

interface ExecutiveData {
  scope: string | null;
  basis: string;
  basis_title: string;
  basis_predicate: string;
  default_basis: string;
  extract: { batch_id: number; as_of: string | null } | null;
  scopes: { scope: string; apps: number }[];
  as_of?: string | null;
  tiles?: { in_scope: number; decided: number; migrating: number; deployed: null };
  deployed_note?: string;
  funnel?: { decided: number; undecided_records: number; open: number; provenance: ProvenanceSegment[] };
  report_out?: { sentences: string[]; note: string };
  universe?: { live: number | null; live_basis: string; extract_rows: number };
  removed?: { key: string; title: string; reason: string | null; available?: boolean }[];
  caveat?: string | null;
}

export default function ExecutiveRollup({ refreshKey, scope, scopeLoaded, basis, onScopeChange }: {
  refreshKey: number;
  scope: string | null;
  scopeLoaded: boolean;
  basis: string;
  onScopeChange: (scope: string) => void;
}) {
  const [data, setData] = useState<ExecutiveData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!scopeLoaded) return undefined;
    let cancelled = false;
    setLoading(true);
    apiClient.get('/api/cloud-portfolio/executive', { params: { scope: scope || undefined, basis } })
      .then((r) => { if (!cancelled) { setData(r.data); setError(null); } })
      .catch((e) => { if (!cancelled) setError(e?.response?.data?.error || e.message || 'Failed to load the rollup'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [scope, scopeLoaded, basis, refreshKey]);

  const pickScope = (next: string) => {
    onScopeChange(next);
    apiClient.put('/api/cloud-portfolio/my-scope', { scope: next }).catch(() => {});
  };

  if (!scopeLoaded || loading) return <p className="text-xs text-gray-500 flex items-center gap-1.5"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading executive rollup…</p>;

  if (error) {
    return (
      <div className="flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5">
        <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
        {error}
      </div>
    );
  }

  if (!data) return null;

  const decidedTotal = data.funnel ? Math.max(1, data.funnel.decided) : 1;

  return (
    <div className="space-y-5">
      <div className="bg-white border border-gray-200 rounded-xl p-4">
        <div className="flex items-center gap-2 mb-1">
          <Presentation className="w-4 h-4 text-amber-500" />
          <span className="text-sm font-semibold text-super-dark-purple">Executive rollup — the 60-second story</span>
        </div>
        <p className="text-[11px] text-gray-400 mb-3">Basis {data.basis} ({data.basis_predicate}) · extract as of {data.extract?.as_of || '—'}</p>
        <div className="flex gap-1.5 flex-wrap">
          {data.scopes.map((s) => (
            <button
              key={s.scope}
              onClick={() => pickScope(s.scope)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors border ${
                s.scope === data.scope
                  ? 'bg-super-dark-purple text-white border-super-dark-purple'
                  : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
              }`}
            >
              {s.scope} · {s.apps}
            </button>
          ))}
        </div>
        {!data.scope && (
          <p className="text-xs text-gray-500 mt-3">Pick a datacentre — every number below is scoped.</p>
        )}
      </div>

      {data.caveat && (
        <div className="flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5">
          <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
          {data.caveat}
        </div>
      )}

      {data.scope && data.tiles && data.funnel && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <p className="text-[11px] text-gray-500">In scope · {data.scope}</p>
              <p className="text-2xl font-bold text-super-dark-purple">{data.tiles.in_scope}</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <p className="text-[11px] text-gray-500">Decided</p>
              <p className="text-2xl font-bold text-super-dark-purple">{data.tiles.decided}</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <p className="text-[11px] text-gray-500">Migrating</p>
              <p className="text-2xl font-bold text-super-dark-purple">{data.tiles.migrating}</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <p className="text-[11px] text-gray-500">Deployed</p>
              <p className="text-2xl font-bold text-gray-300">—</p>
              <p className="text-[10px] text-gray-400 mt-1">{data.deployed_note}</p>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl p-4">
            <p className="text-sm font-semibold text-super-dark-purple mb-1">Decisions by provenance · {data.scope}</p>
            <p className="text-xs text-gray-500 mb-3">How each call was made — rates are measured against the labelled set and recomputed nightly, never hardcoded.</p>
            {data.funnel.decided === 0 ? (
              <p className="text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5">
                No decisions recorded yet — the band fills as gates, the model, and interviews disposition applications. {data.funnel.open} applications are open in this scope.
              </p>
            ) : (
              <div className="flex h-6 rounded-lg overflow-hidden border border-gray-200 mb-3">
                {data.funnel.provenance.filter((p) => p.count > 0).map((p) => (
                  <div
                    key={p.key}
                    style={{ width: `${(p.count / decidedTotal) * 100}%`, background: PROVENANCE_COLOR[p.key] }}
                    className="flex items-center justify-center text-[10px] font-bold text-super-dark-purple min-w-[28px]"
                  >
                    {p.count}
                  </div>
                ))}
              </div>
            )}
            <div className="space-y-1.5">
              {data.funnel.provenance.map((p) => (
                <div key={p.key} className="flex items-start gap-2 text-xs">
                  <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0 mt-0.5 border border-gray-300" style={{ background: PROVENANCE_COLOR[p.key] }} />
                  <span className="font-semibold text-gray-700 whitespace-nowrap">{p.label} · {p.count}</span>
                  <span className="text-gray-500">
                    {p.quality.rate_text}
                    {p.quality.provisional && <span className="ml-1.5 text-[10px] font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded px-1 py-px">PROVISIONAL</span>}
                    {p.quality.insufficient && !p.quality.provisional && <span className="ml-1.5 text-[10px] font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded px-1 py-px">LOW N</span>}
                    {p.quality.note && <span className="text-gray-400"> — {p.quality.note}</span>}
                  </span>
                </div>
              ))}
            </div>
            {data.funnel.undecided_records > 0 && (
              <p className="text-[11px] text-gray-400 mt-2">{data.funnel.undecided_records} explicitly marked undecided · {data.funnel.open} with no decision yet</p>
            )}
          </div>

          {data.report_out && (
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <p className="text-sm font-semibold text-super-dark-purple mb-2">Report-out</p>
              <div className="text-sm text-gray-700 leading-relaxed space-y-1">
                {data.report_out.sentences.map((s, i) => <p key={i}>{s}</p>)}
              </div>
              <p className="text-[11px] text-gray-400 mt-3">{data.report_out.note}</p>
            </div>
          )}

          {data.removed && (
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <p className="text-sm font-semibold text-super-dark-purple mb-2">Removed from this screen deliberately</p>
              <div className="space-y-2">
                {data.removed.filter((r) => r.reason).map((r) => (
                  <div key={r.key} className="text-xs">
                    <span className="font-semibold text-gray-700">{r.title}</span>
                    <span className="text-gray-500"> — {r.reason}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {data.universe && (
            <p className="text-[11px] text-gray-400">
              Live universe {data.universe.live} under {data.universe.live_basis} · full extract {data.universe.extract_rows} records · this view is scoped to {data.scope} under {data.basis} ({data.tiles.in_scope} applications)
            </p>
          )}
        </>
      )}
    </div>
  );
}
